import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import docusignService from "../../services/docusign.service";

// #11 - Docusign signing step. Sits before Review and gates it: the wizard can't
// reach Review until the agent has signed and returned.
//
// This step explains what's about to happen, then (on the agent's click) sends
// them to the signing page. Signing happens off this page: we navigate the whole
// tab to the signing URL (real Docusign in production, our mock page otherwise),
// and Docusign returns the agent to /contract/signing-return/:id, which confirms
// the signature and advances the wizard to Review.
const DocuSign = ({ data }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const goToSigning = () => {
    setLoading(true);
    docusignService
      .startSigning(data)
      .then(({ signingUrl }) => {
        // Full-page navigation, exactly as a real Docusign redirect would be.
        window.location.href = signingUrl;
      })
      .catch((err) => {
        setErrorMessage(
          `There was a problem starting the signing process: ${
            err?.response?.data?.message || err.message
          }`
        );
        setLoading(false);
      });
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Paper sx={{ p: 4, maxWidth: 560, textAlign: "center" }} elevation={2}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6">Sign your contract</Typography>
          <Typography variant="body1" color="text.secondary">
            Next, you'll be taken to DocuSign, an external site, to sign your
            onboarding documents (W-9, direct deposit, and agent agreement). This
            step must be completed before you can continue to review and submit
            your request.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When you finish signing, you'll be brought back here automatically.
          </Typography>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={goToSigning}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? "Taking you to DocuSign..." : "Continue to DocuSign"}
          </Button>
        </Stack>
        {errorMessage && (
          <Snackbar
            open={!!errorMessage}
            onClose={() => setErrorMessage(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setErrorMessage(null)}
              severity="error"
              sx={{ width: "100%" }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>
        )}
      </Paper>
    </Box>
  );
};

export default DocuSign;
